/**
 * CALCULATION TYPES DEFINITION
 * Defines calculation types for transforming raw metric values into derived calculations.
 * Used in Security Analysis Key Metrics and other trended data views.
 */

import { METRICS_REGISTRY } from './metrics';

// ============================================
// CALCULATION TYPE ENUM
// ============================================

export const CALCULATION_TYPES = {
  RAW: 'raw',
  YOY_PERCENT: 'yoy_percent',
  YOY_CHANGE: 'yoy_change',
  QOQ_PERCENT: 'qoq_percent',
  QOQ_CHANGE: 'qoq_change',
  TTM: 'ttm',
  TTM_YOY_PERCENT: 'ttm_yoy_percent',
  TTM_YOY_CHANGE: 'ttm_yoy_change',
  PERCENT_OF_REVENUE: 'percent_of_revenue',
};

// ============================================
// METRIC TYPES (Flow vs Instant)
// ============================================

export const METRIC_TYPES = {
  FLOW: 'flow',       // P&L items that aggregate over time - can use TTM
  INSTANT: 'instant', // Balance sheet/point-in-time snapshots - cannot use TTM
};

// ============================================
// CALCULATION DEFINITIONS
// ============================================

export const CALCULATION_DEFINITIONS = {
  [CALCULATION_TYPES.RAW]: {
    id: CALCULATION_TYPES.RAW,
    label: 'Value',
    shortLabel: 'Value',
    tooltip: 'No transformation - show original values',
    formatOverride: null, // Use original metric format
    constraints: {
      quarterlyOnly: false,
      flowMetricsOnly: false,
      excludeFields: [],
    },
  },
  [CALCULATION_TYPES.YOY_PERCENT]: {
    id: CALCULATION_TYPES.YOY_PERCENT,
    label: 'YoY %',
    shortLabel: 'YoY%',
    tooltip: 'Year-over-year percentage change',
    formatOverride: 'percentage',
    constraints: {
      quarterlyOnly: false,
      flowMetricsOnly: false,
      excludeFields: [],
    },
  },
  [CALCULATION_TYPES.YOY_CHANGE]: {
    id: CALCULATION_TYPES.YOY_CHANGE,
    label: 'YoY #',
    shortLabel: 'YoY#',
    tooltip: 'Year-over-year absolute change',
    formatOverride: null, // Use original metric format
    constraints: {
      quarterlyOnly: false,
      flowMetricsOnly: false,
      excludeFields: [],
    },
  },
  [CALCULATION_TYPES.QOQ_PERCENT]: {
    id: CALCULATION_TYPES.QOQ_PERCENT,
    label: 'QoQ %',
    shortLabel: 'QoQ%',
    tooltip: 'Quarter-over-quarter percentage change',
    formatOverride: 'percentage',
    constraints: {
      quarterlyOnly: true,
      flowMetricsOnly: false,
      excludeFields: [],
    },
  },
  [CALCULATION_TYPES.QOQ_CHANGE]: {
    id: CALCULATION_TYPES.QOQ_CHANGE,
    label: 'QoQ #',
    shortLabel: 'QoQ#',
    tooltip: 'Quarter-over-quarter absolute change',
    formatOverride: null, // Use original metric format
    constraints: {
      quarterlyOnly: true,
      flowMetricsOnly: false,
      excludeFields: [],
    },
  },
  [CALCULATION_TYPES.TTM]: {
    id: CALCULATION_TYPES.TTM,
    label: 'TTM',
    shortLabel: 'TTM',
    tooltip: 'Trailing 12 months (sum of last 4 quarters)',
    formatOverride: null, // Use original metric format
    constraints: {
      quarterlyOnly: true,
      flowMetricsOnly: true,
      excludeFields: [],
    },
  },
  [CALCULATION_TYPES.TTM_YOY_PERCENT]: {
    id: CALCULATION_TYPES.TTM_YOY_PERCENT,
    label: 'TTM YoY %',
    shortLabel: 'TTM%',
    tooltip: 'TTM year-over-year percentage change',
    formatOverride: 'percentage',
    constraints: {
      quarterlyOnly: true,
      flowMetricsOnly: true,
      excludeFields: [],
    },
  },
  [CALCULATION_TYPES.TTM_YOY_CHANGE]: {
    id: CALCULATION_TYPES.TTM_YOY_CHANGE,
    label: 'TTM YoY #',
    shortLabel: 'TTM#',
    tooltip: 'TTM year-over-year absolute change',
    formatOverride: null, // Use original metric format
    constraints: {
      quarterlyOnly: true,
      flowMetricsOnly: true,
      excludeFields: [],
    },
  },
  [CALCULATION_TYPES.PERCENT_OF_REVENUE]: {
    id: CALCULATION_TYPES.PERCENT_OF_REVENUE,
    label: '% of Rev',
    shortLabel: '%Rev',
    tooltip: 'Metric as percentage of Revenue',
    formatOverride: 'percentage',
    constraints: {
      quarterlyOnly: false,
      flowMetricsOnly: false,
      excludeFields: [],
    },
  },
};

// ============================================
// PERCENT OF REVENUE ELIGIBILITY
// ============================================

const PERCENT_OF_REVENUE_ELIGIBLE_FIELDS = new Set(
  Object.values(METRICS_REGISTRY).flatMap(metric => (
    Object.values(metric.variants)
      .filter(variant => variant.metadata?.calc?.includes('% of Revenue'))
      .map(variant => variant.field)
  ))
);

/**
 * Checks the canonical metric registry for explicit % of Revenue support.
 * Unknown fields, including dynamic segment breakout labels, default to false.
 * @param {string} metricField - The canonical display field for the metric
 * @returns {boolean} - Whether the metric explicitly supports % of Revenue
 */
export const supportsPercentOfRevenue = metricField => (
  Boolean(metricField) && PERCENT_OF_REVENUE_ELIGIBLE_FIELDS.has(metricField)
);

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Checks if a calculation type is valid for the given context.
 * @param {string} calculationType - The calculation type ID from CALCULATION_TYPES
 * @param {string} metricField - The field name of the metric
 * @param {string} metricType - 'flow' or 'instant' from METRIC_TYPES
 * @param {boolean} isQuarterly - Whether the current time frame is quarterly
 * @returns {boolean} - Whether the calculation is valid for this context
 */
export const isCalculationValid = (calculationType, metricField, metricType, isQuarterly) => {
  const definition = CALCULATION_DEFINITIONS[calculationType];
  if (!definition) return false;

  const { constraints } = definition;

  // Check quarterly constraint
  if (constraints.quarterlyOnly && !isQuarterly) {
    return false;
  }

  // Check flow metrics constraint
  if (constraints.flowMetricsOnly && metricType !== METRIC_TYPES.FLOW) {
    return false;
  }

  // % of Revenue is opt-in in the canonical metric registry. This keeps
  // percentages, counts, balance-sheet metrics, and unknown fields safe by default.
  if (
    calculationType === CALCULATION_TYPES.PERCENT_OF_REVENUE
    && !supportsPercentOfRevenue(metricField)
  ) {
    return false;
  }

  return true;
};

/**
 * Returns the list of available calculation types for the given context.
 * @param {string} metricField - The field name of the metric
 * @param {string} metricType - 'flow' or 'instant' from METRIC_TYPES
 * @param {boolean} isQuarterly - Whether the current time frame is quarterly
 * @returns {Array} - Array of valid calculation definition objects
 */
export const getAvailableCalculations = (metricField, metricType, isQuarterly) => {
  return Object.values(CALCULATION_DEFINITIONS).filter(def =>
    isCalculationValid(def.id, metricField, metricType, isQuarterly)
  );
};

/**
 * Filters a caller-selected list to calculations valid for one metric and source grain.
 * Used by multi-row views where each metric can support a different subset.
 */
export const filterValidCalculationTypes = (
  calculationTypes,
  metricField,
  metricType,
  isQuarterly
) => (calculationTypes || []).filter(calculationType => (
  isCalculationValid(calculationType, metricField, metricType, isQuarterly)
));

/**
 * Gets the format override for a calculation type, if any.
 * @param {string} calculationType - The calculation type ID
 * @returns {string|null} - The format override or null to use original format
 */
export const getFormatOverride = (calculationType) => {
  const definition = CALCULATION_DEFINITIONS[calculationType];
  return definition?.formatOverride || null;
};
